import './PDFCanvas.css'
import React, { useRef, useEffect, useState } from 'react'
import { PAGE_BORDER_WIDTH } from '../constants'
import {
  getOutputScale,
  setDimensionsOnCanvas,
  areEqual
} from './PDFCanvas.utils.js'
import { usePreviousDistinct, useDebounce } from 'react-use'
const classnames = require('classnames')

const PDFCanvas = ({
  customPageRotation,
  onRendered = () => {},
  page,
  shouldBeRendered,
  viewport,
  viewportHeight,
  viewportWidth
}) => {
  const div = useRef(null)
  const canvas = useRef(null)
  const [maxViewportHeight, setMaxViewportHeight] = useState(null)
  const [isHidden, setIsHidden] = useState(false)

  const prevMaxViewportHeight = usePreviousDistinct(maxViewportHeight)
  useEffect(() => {
    if (prevMaxViewportHeight < viewportHeight) {
      setMaxViewportHeight(viewportHeight)
    }
  })

  const prepareForRender = () => {
    const childNodesToResize = [...div.current.childNodes]
    childNodesToResize.forEach(node => {
      node.style.width = `${viewportWidth}px`
      node.style.height = `${viewportHeight}px`
    })
    const childNodesToRemoveBefore = [...div.current.childNodes].slice(0, -1)
    childNodesToRemoveBefore.forEach(node => {
      node.parentNode.removeChild(node)
    })
  }

  const renderPage = () => {
    canvas.current = document.createElement('canvas')
    canvas.current.className = 'pageContainer'

    const canvasContext = canvas.current.getContext('2d', {
      alpha: false
    })
    const viewport_ = viewport.clone({
      scale: viewport.scale * getOutputScale(canvas.current)
    })

    setDimensionsOnCanvas(canvas.current, viewport_.height, viewport_.width)
    page.render({ canvasContext, viewport: viewport_ }).promise.then(() => {
      if (div.current instanceof HTMLElement) {
        div.current.insertBefore(canvas.current, div.current.firstChild)
        if (div.current.childNodes.length > 1) {
          div.current.removeChild(div.current.lastChild)
        }
      }
      setIsHidden(false)
      onRendered()
    })
  }

  useEffect(() => {
    if (viewport && shouldBeRendered) {
      prepareForRender()
    }
    return () => {
      if (page) {
        page.cleanup()
      }
    }
  }, [page])

  React.useLayoutEffect(() => {
    if (viewport && shouldBeRendered) {
      setIsHidden(true)
    }
  }, [customPageRotation])

  useDebounce(
    () => {
      if (viewport && shouldBeRendered) {
        prepareForRender()
        renderPage()
      }
    },
    275,
    [customPageRotation, viewport, shouldBeRendered, page]
  )

  const classes = classnames('Page-canvas-container', {
    'Page-canvas-container--hidden': isHidden
  })

  return (
    <div
      className={classes}
      ref={div}
      style={{
        height: viewportHeight + PAGE_BORDER_WIDTH * 2,
        width: viewportWidth + PAGE_BORDER_WIDTH * 2
      }}
    />
  )
}

export default React.memo(PDFCanvas, areEqual)
